import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTaxi } from '@fortawesome/free-solid-svg-icons';
import { Capacitor } from '@capacitor/core';
import { SplashScreen as CapacitorSplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';
import './App.css';

// Tüm sayfaları import edelim
import Home from './pages/Home';
import Login from './pages/Login';
import SmsVerification from './pages/SmsVerification';
import VerifySms from './pages/VerifySms';
import PlakaListesi from './pages/PlakaListesi';
import Tahsilatlar from './pages/Tahsilatlar';
import PaymentSimple from './pages/PaymentSimple';
import Soforlerim from './pages/Soforlerim';
import Taksimetre from './pages/Taksimetre';
import UcusBilgileri from './pages/UcusBilgileri';
import HavaDurumu from './pages/HavaDurumu';
import TrafikHaritasi from './pages/TrafikHaritasi';
import IstekSikayet from './pages/IstekSikayet';
import Duyurular from './pages/Duyurular';
import Iletisim from './pages/Iletisim';
import Baskan from './pages/Baskan';
import SplashScreen from './pages/SplashScreen';
import Dashboard from './pages/Dashboard';

import Profile from './pages/Profile';
import QrKodOkuma from './pages/QrKodOkuma';
import AracDegerlendirme from './pages/AracDegerlendirme';
import TestPages from './pages/TestPages';
import PlakaGecislerim from './pages/PlakaGecislerim';
import YakitHareketlerim from './pages/YakitHareketlerim';
import ErrorBoundary from './components/ErrorBoundary';

// Modern Splash Screen bileşeni
const SplashScreenComponent = () => (
  <div className="modern-splash-screen">
    <div className="splash-content">
      <div className="logo-section">
        <img 
          src="/images/logo2024.png" 
          alt="İstanbul Havalimanı Taksi Kooperatifi" 
          className="splash-logo"
        />
      
      </div>
      
      <div className="loading-section">
        <div className="taxi-animation">
          <FontAwesomeIcon icon={faTaxi} className="taxi-icon" />
          <div className="road-line"></div>
        </div>
        <div className="loading-text">Uygulama başlatılıyor...</div>
        <div className="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
      
      <div className="copyright-section">
        <p>© 2024 Tüm Hakları Saklıdır</p>
        <p>Powered by MuratSoft</p>
      </div>
    </div>
  </div>
);

// Placeholder bileşeni (Henüz oluşturulmamış sayfalar için)
const PlaceholderPage: React.FC<{title: string}> = ({title}) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    padding: '20px',
    textAlign: 'center'
  }}>
    <h1>{title}</h1>
    <p>Bu sayfa yapım aşamasındadır.</p>
    <button onClick={() => window.history.back()} style={{
      padding: '10px 20px',
      marginTop: '20px',
      cursor: 'pointer'
    }}>
      Geri Dön
    </button>
  </div>
);

const WebApp: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const initializeApp = async () => {
      // Native platform kontrolü
      if (Capacitor.isNativePlatform()) {
        // StatusBar ayarları
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setBackgroundColor({ color: '#ffffff' });
        
        // Keyboard ayarları
        Keyboard.addListener('keyboardWillShow', () => {
          document.body.classList.add('keyboard-is-open');
        });
        
        Keyboard.addListener('keyboardWillHide', () => {
          document.body.classList.remove('keyboard-is-open');
        });
      }
      
      // Splash screen'i göster ve 2 saniye sonra kaldır
      const timer = setTimeout(async () => {
        if (Capacitor.isNativePlatform()) {
          await CapacitorSplashScreen.hide();
        }
        setIsLoading(false);
      }, 2000);

      // Oturum durumu kontrolü
      const checkAuth = () => {
        const token = localStorage.getItem('authToken');
        const isAuth = localStorage.getItem('isAuthenticated') === 'true';
        
        // localStorage'dan oturum durumunu kontrol et
        if (token && isAuth) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      };

      checkAuth();
      return () => clearTimeout(timer);
    };

    initializeApp();
  }, []);

  if (isLoading) {
    return <SplashScreenComponent />;
  }

  return (
    <Router>
      <Routes>
        {/* Oturum gerektirmeyen rotalar */}
        <Route path="/login" element={<Login />} />
        <Route path="/sms-verification" element={<SmsVerification setIsAuthenticated={setIsAuthenticated} />} />
        <Route path="/verify-sms" element={<VerifySms setIsAuthenticated={setIsAuthenticated} />} />
        
        {/* Ana sayfa ve diğer sayfalar */}
        <Route path="/" element={<Home />} />
        <Route path="/home" element={<Home />} />

        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/plaka-listesi" element={<ErrorBoundary><PlakaListesi /></ErrorBoundary>} />
        <Route path="/tahsilatlar" element={<ErrorBoundary><Tahsilatlar /></ErrorBoundary>} />
        <Route path="/odeme" element={<ErrorBoundary><PaymentSimple /></ErrorBoundary>} />
        <Route path="/soforlerim" element={<ErrorBoundary><Soforlerim /></ErrorBoundary>} />
        <Route path="/ucus-bilgileri" element={<UcusBilgileri />} />
        <Route path="/hava-durumu" element={<HavaDurumu />} />
        <Route path="/trafik-haritasi" element={<TrafikHaritasi />} />
        <Route path="/taksimetre" element={<Taksimetre />} />
        <Route path="/istek-sikayet" element={<IstekSikayet />} />
        <Route path="/duyurular" element={<Duyurular />} />
        <Route path="/iletisim" element={<Iletisim />} />
        <Route path="/baskan" element={<Baskan />} />
        <Route path="/qr-kod-okuma" element={<QrKodOkuma />} />
        <Route path="/arac-degerlendirme" element={<AracDegerlendirme />} />
        <Route path="/test" element={<TestPages />} />
        
        {/* UYE Grubu Özel Sayfalar */}
        <Route path="/plaka-gecislerim" element={<ErrorBoundary><PlakaGecislerim /></ErrorBoundary>} />
        <Route path="/yakit-hareketlerim" element={<ErrorBoundary><YakitHareketlerim /></ErrorBoundary>} />
        
        {/* Henüz oluşturulmamış sayfalar için placeholder */}
        <Route path="/profil" element={<Profile />} />
        <Route path="/ayarlar" element={<PlaceholderPage title="Ayarlar" />} />
        <Route path="/bildirimler" element={<PlaceholderPage title="Bildirimler" />} />
        
        {/* Bilinmeyen yollar ana sayfaya yönlendirilir */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

export default WebApp; 